import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, ListChecks, CheckCircle2, Circle, AlertTriangle, Camera } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";

const SEVERITY = [
  { v: "low", label: "Low", color: "bg-zinc-100 text-zinc-700 border-zinc-300" },
  { v: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-900 border-yellow-300" },
  { v: "high", label: "High", color: "bg-orange-100 text-orange-900 border-orange-300" },
  { v: "critical", label: "Critical", color: "bg-red-100 text-red-900 border-red-300" },
];

const STATUSES = [
  { v: "open", label: "Open" },
  { v: "in_progress", label: "In progress" },
  { v: "fixed", label: "Fixed" },
  { v: "signed_off", label: "Signed off" },
];

export function PunchListTab({ jobId, project }) {
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ status: "open" });
  const [open, setOpen] = useState(null); // 'new' | item | null

  const load = async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const params = { job_id: jobId };
      if (filter.status) params.status = filter.status;
      const { data } = await api.get("/pm/punch", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId, filter.status]);
  useEffect(() => { api.get("/team/directory").then(({ data }) => setTeam(data || [])).catch(() => {}); }, []);

  const stats = useMemo(() => ({
    open: items.filter((i) => i.status === "open").length,
    in_progress: items.filter((i) => i.status === "in_progress").length,
    fixed: items.filter((i) => i.status === "fixed").length,
    signed_off: items.filter((i) => i.status === "signed_off").length,
  }), [items]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/pm/punch/${id}`, { status });
      toast.success(status === "signed_off" ? "Signed off ✓" : `Status: ${status}`);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this punch item?")) return;
    await api.delete(`/pm/punch/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => {
          const on = filter.status === s.v;
          return (
            <button key={s.v} onClick={() => setFilter({ ...filter, status: on ? "" : s.v })} className={`px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider border-2 rounded-sm transition-colors ${on ? "border-[#FF5F15] bg-[#FF5F15] text-white" : "border-zinc-300 text-zinc-600 hover:border-zinc-400"}`} data-testid={`pm-punch-filter-${s.v}`}>
              {s.label}
            </button>
          );
        })}
        <button onClick={() => setFilter({})} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-[#FF5F15] ml-1" data-testid="pm-punch-filter-clear">
          Show all
        </button>
        <div className="flex-1" />
        <Button onClick={() => setOpen("new")} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="pm-punch-new">
          <Plus className="w-3.5 h-3.5 mr-2" /> New punch item
        </Button>
      </div>

      {!loading && items.length === 0 && (
        <div className="hard-card p-10 text-center">
          <ListChecks className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-500">No matching punch items for <b>{project?.title}</b>.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((p) => (
          <PunchCard key={p.id} item={p} onOpen={() => setOpen(p)} onStatus={updateStatus} onDelete={del} />
        ))}
      </div>

      {open && (
        <PunchEditor
          jobId={jobId}
          initial={open === "new" ? null : open}
          team={team}
          onClose={() => setOpen(null)}
          onSaved={() => { setOpen(null); load(); }}
        />
      )}
    </div>
  );
}

function PunchCard({ item, onOpen, onStatus, onDelete }) {
  const sev = SEVERITY.find((s) => s.v === item.severity) || SEVERITY[1];
  const isSigned = item.status === "signed_off";
  return (
    <div className={`hard-card p-4 ${isSigned ? "opacity-60" : ""}`} data-testid={`pm-punch-card-${item.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={() => onStatus(item.id, isSigned ? "open" : "signed_off")} className="mt-0.5 shrink-0" title={isSigned ? "Reopen" : "Sign off"} data-testid={`pm-punch-toggle-${item.id}`}>
          {isSigned ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5 text-zinc-300 hover:text-zinc-500" />}
        </button>
        <button onClick={onOpen} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] font-bold uppercase tracking-widest bg-[#09090B] text-white px-1.5 py-0.5">{item.number}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${sev.color}`}>{sev.label}</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{item.status?.replace("_", " ")}</span>
          </div>
          <div className={`font-bold text-sm mt-1.5 ${isSigned ? "line-through" : ""}`}>{item.title}</div>
          {item.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{item.description}</div>}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-zinc-500 flex-wrap">
            {item.location && <span>📍 {item.location}</span>}
            {item.assignee_name && <span>👤 {item.assignee_name}</span>}
            {item.due_date && <span>📅 {item.due_date}</span>}
            {item.photo_paths?.length > 0 && <span><Camera className="w-3 h-3 inline" /> {item.photo_paths.length}</span>}
          </div>
        </button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 shrink-0" onClick={() => onDelete(item.id)} data-testid={`pm-punch-delete-${item.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function PunchEditor({ jobId, initial, team, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    location: initial?.location || "",
    severity: initial?.severity || "medium",
    assignee_id: initial?.assignee_id || "",
    due_date: initial?.due_date || "",
    status: initial?.status || "open",
    sign_off_note: initial?.sign_off_note || "",
  });
  const [busy, setBusy] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [savedId, setSavedId] = useState(initial?.id || null);
  const [photos, setPhotos] = useState(initial?.photo_paths || []);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      if (savedId) {
        await api.patch(`/pm/punch/${savedId}`, form);
        toast.success("Saved");
      } else {
        const { data } = await api.post("/pm/punch", { job_id: jobId, ...form });
        setSavedId(data.id);
        toast.success(`Created ${data.number}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const uploadPhoto = async () => {
    if (!savedId) { toast.error("Save first, then add photos"); return; }
    if (!photoFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", photoFile);
      const { data } = await api.post(`/pm/punch/${savedId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.storage_path]);
      setPhotoFile(null);
      toast.success("Photo added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-xl max-h-[92vh] overflow-y-auto" data-testid="pm-punch-editor">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">{isEdit ? `Edit ${initial.number}` : "New punch item"}</DialogTitle>
          <DialogDescription>Snag this so it doesn&apos;t end up in the final defect list.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tag">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. Chip in kerb at 24+50" data-testid="pm-punch-title" />
          </div>
          <div>
            <Label className="label-tag">Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-punch-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="Station / area" data-testid="pm-punch-location" />
            </div>
            <div>
              <Label className="label-tag">Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-punch-due" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Severity</Label>
              <div className="mt-2 flex flex-wrap gap-1">
                {SEVERITY.map((s) => {
                  const on = form.severity === s.v;
                  return (
                    <button key={s.v} type="button" onClick={() => setForm({ ...form, severity: s.v })} className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider border-2 rounded-sm ${on ? "border-[#FF5F15] bg-[#FF5F15] text-white" : "border-zinc-300 text-zinc-600 hover:border-zinc-400"}`} data-testid={`pm-punch-sev-${s.v}`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="label-tag">Assignee</Label>
              <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} className="mt-2 w-full rounded-sm border border-zinc-400 px-3 py-2 text-sm bg-white" data-testid="pm-punch-assignee">
                <option value="">— unassigned —</option>
                {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="label-tag">Status</Label>
            <div className="mt-2 flex flex-wrap gap-1">
              {STATUSES.map((s) => {
                const on = form.status === s.v;
                return (
                  <button key={s.v} type="button" onClick={() => setForm({ ...form, status: s.v })} className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider border-2 rounded-sm ${on ? "border-[#FF5F15] bg-[#FF5F15] text-white" : "border-zinc-300 text-zinc-600 hover:border-zinc-400"}`} data-testid={`pm-punch-status-${s.v}`}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
          {form.status === "signed_off" && (
            <div>
              <Label className="label-tag">Sign-off note</Label>
              <Input value={form.sign_off_note} onChange={(e) => setForm({ ...form, sign_off_note: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-punch-signoff-note" />
            </div>
          )}

          {/* Photos */}
          <div>
            <Label className="label-tag">Photos {savedId ? `(${photos.length})` : "(save first)"}</Label>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                {photos.map((p) => <AuthImage key={p} path={p} className="w-full h-20 object-cover border border-zinc-200" />)}
              </div>
            )}
            {savedId && (
              <div className="mt-2 flex items-center gap-2">
                <Input type="file" accept="image/*" capture="environment" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} className="rounded-sm border-zinc-400 text-xs" data-testid="pm-punch-photo-pick" />
                <Button size="sm" variant="outline" onClick={uploadPhoto} disabled={!photoFile || busy} className="rounded-sm h-9 text-xs" data-testid="pm-punch-photo-upload">Upload</Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" disabled={busy}>Close</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="pm-punch-save">
            {savedId ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
